import { FormulaCatalogoGrupoStruct } from '@shared/services/structs/formula/formula-catalogo-grupo.struct';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';

export function listarFormulasContexto(contexto: FormulaCatalogoGrupoStruct): FormulaCatalogoItemStruct[] {
  // Preserva ocorrências até resolver os critérios: a mesma fórmula pode ter vínculos distintos.
  return [...contexto.formulas, ...contexto.filhos.flatMap(listarFormulasContexto)];
}

export function quantidadeFormulasContexto(contexto: FormulaCatalogoGrupoStruct): number {
  return (
    contexto.formulas.length +
    contexto.filhos.reduce((quantidade, filho) => quantidade + quantidadeFormulasContexto(filho), 0)
  );
}

export function possuiFormulasContexto(contexto: FormulaCatalogoGrupoStruct): boolean {
  return quantidadeFormulasContexto(contexto) > 0;
}

export function selecionarFormulasPorCriterio(
  formulas: FormulaCatalogoItemStruct[],
  criterio: string,
  codigo: string | null
): FormulaCatalogoItemStruct[] {
  const selecionadas = formulas.filter(formula => {
    const valores = formula.criteriosContexto?.[criterio];
    return !valores || (codigo != null && valores.includes(codigo));
  });
  const resultado = new Map<string, FormulaCatalogoItemStruct>();
  for (const formula of selecionadas) {
    const anterior = resultado.get(formula.chaveFormula);
    if (!anterior) {
      resultado.set(formula.chaveFormula, formula);
      continue;
    }
    const variaveis = [
      ...new Map(
        [...anterior.variaveis, ...formula.variaveis].map(variavel => [variavel.chave, variavel])
      ).values(),
    ];
    resultado.set(formula.chaveFormula, { ...anterior, variaveis, variaveisPermitidas: variaveis });
  }
  return [...resultado.values()];
}

/**
 * Variáveis que uma observação (natureza, exceção ou regra tributária) pode citar a partir das fórmulas: a variável
 * de saída de cada fórmula e as permitidas, sem repetição. Mesma regra da API (FormulaCatalogoContextoHelper.ListarVariaveis).
 */
export function listarVariaveisObservacao(formulas: FormulaCatalogoItemStruct[]): VariavelStruct[] {
  const todas = formulas.flatMap(formula => [
    ...(formula.variavelPrincipal ? [formula.variavelPrincipal] : []),
    ...(formula.variaveisPermitidas ?? formula.variaveis),
  ]);
  return todas.filter((v, i, arr) => arr.findIndex(x => (x.chave || x.tagXml || x.nome) === (v.chave || v.tagXml || v.nome)) === i);
}

export function indexarFormulasContextos(
  contextos: FormulaCatalogoGrupoStruct[]
): Record<string, FormulaCatalogoItemStruct[]> {
  const resultado: Record<string, FormulaCatalogoItemStruct[]> = {};
  for (const contexto of contextos) {
    resultado[contexto.chave] = listarFormulasContexto(contexto);
    Object.assign(resultado, indexarFormulasContextos(contexto.filhos));
  }
  return resultado;
}

export function filtrarContextos(
  contextos: FormulaCatalogoGrupoStruct[],
  termo: string
): FormulaCatalogoGrupoStruct[] {
  return contextos
    .map(contexto => {
      if (contexto.grupo.toLowerCase().includes(termo)) return contexto;
      return {
        ...contexto,
        formulas: contexto.formulas.filter(formula =>
          [
            formula.descricao,
            formula.variavelPrincipal?.nome,
            formula.variavelPrincipal?.chave,
            ...formula.variaveis.flatMap(variavel => [variavel.nome, variavel.chave, variavel.descricao]),
          ]
            .join(' ')
            .toLowerCase()
            .includes(termo)
        ),
        filhos: filtrarContextos(contexto.filhos, termo),
      };
    })
    .filter(contexto => contexto.formulas.length > 0 || contexto.filhos.length > 0);
}
